import { create } from 'zustand';
import type {
  CatalogOptionDto,
  CatalogOptionInput,
  CatalogOptionValueDto,
  CatalogOptionValueInput,
  ColorFamilyCode,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * The options library (ADR-052; API → Store → Render, ADR-008).
 *
 * Options — colour, "sold as", size — and their values, named once for the
 * whole store. The screen shows one option at a time: its values searched and
 * paged on the server, and a colour's filtered by family.
 *
 * Beside the page the operator reads, the store keeps a `summary` of the open
 * option: every value, walked a hundred at a time. A library is a few dozen
 * values, and two questions need all of them rather than one page — how many
 * joined from an import without a name, and which colours have no family yet
 * (the ones the "suggest families" sheet files, so the storefront's colour
 * filter stops offering one entry per product).
 */

export type FamilyFilter = ColorFamilyCode | '';

export interface ValuesSummary {
  optionId: string;
  values: CatalogOptionValueDto[];
}

interface OptionsState {
  options: CatalogOptionDto[];
  optionsLoading: boolean;
  optionsLoaded: boolean;

  /** Whose values `values` are. */
  optionId: string | null;
  values: CatalogOptionValueDto[];
  nextCursor: string | null;
  search: string;
  family: FamilyFilter;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;

  summary: ValuesSummary | null;
  summaryLoading: boolean;

  saving: boolean;
  /** Why the last save failed — shown in the sheet it came from. */
  saveError: string | null;
  /** The families being filed: how far through. */
  filing: { done: number; total: number } | null;

  loadOptions: () => Promise<void>;
  /** The new option, or null with the reason in `saveError`. */
  createOption: (body: CatalogOptionInput) => Promise<CatalogOptionDto | null>;
  updateOption: (id: string, body: Partial<CatalogOptionInput>) => Promise<boolean>;

  /** One option's values; a filter left out keeps the current one. */
  loadValues: (optionId: string, filters?: { search?: string; family?: FamilyFilter }) => Promise<void>;
  loadMoreValues: () => Promise<void>;
  /** Walks every value of the option, for the counts and the family suggestions. */
  loadSummary: (optionId: string) => Promise<void>;

  /** Creates a value when `valueId` is null. True once saved. */
  saveValue: (optionId: string, valueId: string | null, body: CatalogOptionValueInput) => Promise<boolean>;
  /** Files each value under its family, one at a time. True when all were filed. */
  fileFamilies: (optionId: string, entries: { valueId: string; family: ColorFamilyCode }[]) => Promise<boolean>;
  clearSaveError: () => void;
}

const PAGE_SIZE = 24;
const SUMMARY_PAGE_SIZE = 100;
/** A ceiling on the walk, so a library that outgrew "a few dozen" cannot hold the screen. */
const SUMMARY_MAX_PAGES = 20;

// Only the newest request for a list may write it: switching tabs, or typing,
// while a slower answer is on its way must not show the previous option's values.
let valuesRequest = 0;
let summaryRequest = 0;

export const useOptionsStore = create<OptionsState>((set, get) => ({
  options: [],
  optionsLoading: false,
  optionsLoaded: false,

  optionId: null,
  values: [],
  nextCursor: null,
  search: '',
  family: '',
  loading: false,
  loadingMore: false,
  error: null,

  summary: null,
  summaryLoading: false,

  saving: false,
  saveError: null,
  filing: null,

  async loadOptions() {
    set({ optionsLoading: true, error: null });
    try {
      const page = await withSession(() => adminApi.options.list());
      set({ options: page.items, optionsLoading: false, optionsLoaded: true });
    } catch (error) {
      set({ optionsLoading: false, optionsLoaded: true, error: errorMessage(error, 'Could not load the options.') });
    }
  },

  async createOption(body) {
    set({ saving: true, saveError: null });
    try {
      const option = await withSession(() => adminApi.options.create(body));
      set((state) => ({ saving: false, options: [...state.options, option] }));
      return option;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not create the option.') });
      return null;
    }
  },

  async updateOption(id, body) {
    set({ saving: true, saveError: null });
    try {
      const option = await withSession(() => adminApi.options.update(id, body));
      set((state) => ({ saving: false, options: state.options.map((entry) => (entry.id === id ? option : entry)) }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the option.') });
      return false;
    }
  },

  async loadValues(optionId, filters = {}) {
    const state = get();
    const switching = state.optionId !== optionId;
    const search = filters.search ?? (switching ? '' : state.search);
    const family = filters.family ?? (switching ? '' : state.family);
    const request = ++valuesRequest;

    set({
      optionId,
      search,
      family,
      loading: true,
      error: null,
      // Another option's values are never shown under this one's tab.
      ...(switching ? { values: [], nextCursor: null } : {}),
    });

    try {
      const page = await withSession(() =>
        adminApi.options.values(optionId, {
          limit: PAGE_SIZE,
          search: search || undefined,
          family: family || undefined,
          includeInactive: true,
        }),
      );
      if (request !== valuesRequest) return;
      set({ values: page.items, nextCursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      if (request !== valuesRequest) return;
      set({ loading: false, error: errorMessage(error, 'Could not load the values.') });
    }
  },

  async loadMoreValues() {
    const { optionId, nextCursor, loadingMore, search, family } = get();
    if (!optionId || !nextCursor || loadingMore) return;
    const request = valuesRequest;
    set({ loadingMore: true });

    try {
      const page = await withSession(() =>
        adminApi.options.values(optionId, {
          limit: PAGE_SIZE,
          cursor: nextCursor,
          search: search || undefined,
          family: family || undefined,
          includeInactive: true,
        }),
      );
      // A new search started while this page was on its way: it is not this list's page.
      if (request !== valuesRequest) return set({ loadingMore: false });
      set((state) => ({
        values: [...state.values, ...page.items],
        nextCursor: page.meta.nextCursor,
        loadingMore: false,
      }));
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more values.') });
    }
  },

  async loadSummary(optionId) {
    const request = ++summaryRequest;
    set((state) => ({
      summaryLoading: true,
      summary: state.summary?.optionId === optionId ? state.summary : null,
    }));

    try {
      const values: CatalogOptionValueDto[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < SUMMARY_MAX_PAGES; page += 1) {
        const result = await withSession(() =>
          adminApi.options.values(optionId, { limit: SUMMARY_PAGE_SIZE, cursor, includeInactive: true }),
        );
        values.push(...result.items);
        if (!result.meta.nextCursor) break;
        cursor = result.meta.nextCursor;
      }
      if (request !== summaryRequest) return;
      set({ summary: { optionId, values }, summaryLoading: false });
    } catch {
      // The counts are a convenience; the list itself says what went wrong.
      if (request !== summaryRequest) return;
      set({ summaryLoading: false });
    }
  },

  async saveValue(optionId, valueId, body) {
    set({ saving: true, saveError: null });

    try {
      if (valueId) {
        // A code is fixed once created; the API refuses one on an edit.
        const changes: Partial<CatalogOptionValueInput> = { ...body };
        delete changes.code;
        const updated = await withSession(() => adminApi.options.updateValue(valueId, changes));
        const { family } = get();
        set((state) => ({
          saving: false,
          values: state.values
            // Refiled out of the family being shown: it leaves this view, as a reload would take it.
            .filter((entry) => entry.id !== valueId || !family || updated.family === family)
            .map((entry) => (entry.id === valueId ? updated : entry)),
        }));
      } else {
        await withSession(() => adminApi.options.createValue(optionId, body));
        set((state) => ({
          saving: false,
          options: state.options.map((entry) =>
            entry.id === optionId ? { ...entry, valueCount: entry.valueCount + 1 } : entry,
          ),
        }));
        // A new value takes the library's last place; the list is read again
        // rather than guessing where that falls among the pages not yet loaded.
        if (get().optionId === optionId) void get().loadValues(optionId);
      }
      void get().loadSummary(optionId);
      return true;
    } catch (error) {
      // "That code is already one of its values" comes back from the API in words.
      set({ saving: false, saveError: errorMessage(error, 'Could not save the value.') });
      return false;
    }
  },

  async fileFamilies(optionId, entries) {
    set({ saving: true, saveError: null, filing: { done: 0, total: entries.length } });

    let failed: string | null = null;
    // One at a time: each save rewrites that colour on every product using it,
    // and a burst of them in parallel is a burst of those rewrites at once.
    for (const [index, entry] of entries.entries()) {
      try {
        await withSession(() => adminApi.options.updateValue(entry.valueId, { family: entry.family }));
      } catch (error) {
        failed = errorMessage(error, 'Could not file every colour.');
        break;
      }
      set({ filing: { done: index + 1, total: entries.length } });
    }

    set({ saving: false, filing: null, saveError: failed });
    if (get().optionId === optionId) void get().loadValues(optionId);
    void get().loadSummary(optionId);
    return failed === null;
  },

  clearSaveError() {
    set({ saveError: null });
  },
}));
